import { formatTimestampDateKey, timestampTimeZone } from '../../../shared/formatTimestampDate';

export {
  cloneRequestApprovalSteps,
  workflowRequestComposerFingerprint,
  workflowRequestDraftApprovalStepSnapshot,
  workflowDraftContentSnapshot,
  workflowDraftResponseMatchesSnapshot,
} from './workflowDraftModel';
export type {
  WorkflowSubmitIntent,
  DraftSaveStatus,
  DraftSaveConflict,
  WorkflowDraftSnapshot,
  PersistedWorkflowDraft,
  PendingWorkflowSubmitIntent,
} from './workflowDraftModel';
import type {
  WorkflowApprovalStepDefinition,
  WorkflowFieldDefinition,
  WorkflowRequest,
  WorkflowRequestDraftApprovalStep,
  WorkflowTask,
} from '../model';
import type {
  WorkflowApprovalRoleOption,
} from '../approval-settings/public';
import type { WorkflowApprovalLinePreview } from '../WorkflowRequestComposerUI';
import type { EmployeeDirectorySnapshot } from '../../people/public';
import type {
  PillTabItem,
  WorkspaceSurfacePresentation,
} from '../../../design-system';
import { normalizeWorkflowApprovalSteps } from '../workflowApprovalStages';
import {
  approvalDescription,
} from './workflowTemplatePageModel';
import type { WorkflowPersonOption } from '../WorkflowReferencePickers';

export type WorkflowComposerPresentation = WorkspaceSurfacePresentation;

export function workflowRequestTitle(request: Pick<WorkflowRequest, 'title' | 'templateName'>): string {
  return request.title.trim() || request.templateName.trim() || '제목 없는 요청';
}

export function workflowTaskTitle(task: Pick<WorkflowTask, 'subject' | 'document'>): string {
  return task.document?.descriptor?.trim() || task.subject.descriptor?.trim() || '제목 없는 요청';
}

export type ApprovalLineDialogView = 'edit' | 'templates';
export const approvalLineDialogTabs: PillTabItem<ApprovalLineDialogView>[] = [
  { value: 'edit', label: '직접 수정', tabId: 'request-approval-line-edit-tab', panelId: 'request-approval-line-edit-panel' },
  { value: 'templates', label: '승인 템플릿', tabId: 'request-approval-line-templates-tab', panelId: 'request-approval-line-templates-panel' },
];

export function workflowPersonOptions(directory: EmployeeDirectorySnapshot | undefined): WorkflowPersonOption[] {
  if (!directory) return [];
  const organizations = new Map(directory.organizations.map((organization) => [organization.id, organization.name]));
  return directory.members.map((member) => ({
    personId: member.personId,
    workerId: member.id,
    name: member.name,
    title: member.title,
    organizationName: member.organizationId ? organizations.get(member.organizationId) : undefined,
    profilePhotoUrl: member.profilePhotoUrl,
  }));
}


export function workflowRequestIDFromLocation(): string | undefined {
  const requestID = new URLSearchParams(window.location.search).get('request')?.trim();
  return requestID || undefined;
}

export function workflowRequestLoadingPlaceholder(requestID: string): WorkflowRequest {
  return {
    id: requestID,
    templateId: '',
    templateVersionId: '',
    templateName: '워크플로우 요청',
    templateVersion: 1,
    title: '요청 상세',
    status: 'IN_PROGRESS',
    requesterPersonId: '',
    createdAt: '',
    updatedAt: '',
  };
}

export function reorderRequestSteps(steps: WorkflowRequestDraftApprovalStep[]) { return normalizeWorkflowApprovalSteps(steps); }
export function requestApprovalStepsFromTemplate(steps: WorkflowApprovalStepDefinition[]): WorkflowRequestDraftApprovalStep[] {
  return reorderRequestSteps(steps.map(({ id, ...step }) => ({ ...step, sourceTemplateStepId: id, resolverConfig: { ...(step.resolverConfig ?? {}) }, requesterEditable: step.requesterEditable })));
}
export function workflowRequestApprovalStepError(
  step: WorkflowRequestDraftApprovalStep,
  index: number,
  fields: readonly WorkflowFieldDefinition[],
): string | undefined {
  const prefix = `${index + 1}단계`;
  const label = step.label.trim();
  if (!label) return `${prefix} 이름을 입력하세요.`;
  if (Array.from(label).length > 120) return `${prefix} 이름은 120자 이내로 입력하세요.`;

  const approvalLabelId = (step.approvalLabelId ?? step.approvalLabel?.id ?? '').trim();
  if (approvalLabelId && !/^[0-9a-f]{32}$/.test(approvalLabelId)) return `${prefix}의 승인 라벨을 다시 선택하세요.`;

  if (!['REQUESTER_ORG_LEADER', 'SUBJECT_ORG_LEADER', 'ROOT_ORG_LEADER', 'FIXED_PERSON', 'FIXED_ROLE', 'FORM_PERSON_FIELD'].includes(step.resolverType)) {
    return `${prefix}의 승인자 기준을 다시 선택하세요.`;
  }
  if (!['FAIL', 'SKIP', 'FALLBACK_ROOT_ORG_LEADER'].includes(step.missingPolicy)
    || !['FAIL', 'SKIP'].includes(step.selfPolicy)
    || !['KEEP', 'SKIP'].includes(step.duplicatePolicy)) {
    return `${prefix}의 승인자 처리 정책을 다시 선택하세요.`;
  }

  const config = step.resolverConfig ?? {};
  if (step.resolverType === 'REQUESTER_ORG_LEADER' || step.resolverType === 'SUBJECT_ORG_LEADER') {
    const level = config.level ?? 1;
    if (typeof level !== 'number' || !Number.isInteger(level) || level < 1 || level > 20) {
      return `${prefix}의 조직 단계는 1 이상 20 이하의 정수로 입력하세요.`;
    }
  }
  if (step.resolverType === 'SUBJECT_ORG_LEADER' || step.resolverType === 'FORM_PERSON_FIELD') {
    const fieldKey = typeof config.fieldKey === 'string' ? config.fieldKey.trim() : '';
    if (!fieldKey || !fields.some((field) => field.key === fieldKey && field.type === 'PERSON')) {
      return `${prefix}의 구성원 필드를 다시 선택하세요.`;
    }
  }
  if (step.resolverType === 'FIXED_PERSON' && (typeof config.personId !== 'string' || !config.personId.trim())) {
    return `${prefix}의 구성원을 지정하세요.`;
  }
  if (step.resolverType === 'FIXED_ROLE' && (typeof config.roleCode !== 'string' || !config.roleCode.trim())) {
    return `${prefix}의 승인 역할을 선택하세요.`;
  }
  if (step.dueAfterMinutes != null && (
    typeof step.dueAfterMinutes !== 'number'
    || !Number.isInteger(step.dueAfterMinutes)
    || step.dueAfterMinutes < 1
    || step.dueAfterMinutes > 2_147_483_647
  )) return `${prefix}의 처리 기한은 1 이상 2,147,483,647 이하의 정수로 입력하세요.`;
  return undefined;
}
export function approvalLinePreviews(
  steps: readonly WorkflowRequestDraftApprovalStep[],
  directory: EmployeeDirectorySnapshot | undefined,
  requesterWorkerId: string | undefined,
  roleOptions: readonly WorkflowApprovalRoleOption[],
  state: { directoryLoading: boolean; directoryError?: string; rolesLoading: boolean; rolesError?: string },
): WorkflowApprovalLinePreview[] {
  const members = new Map((directory?.members ?? []).map((member) => [member.id, member]));
  const membersByPerson = new Map((directory?.members ?? []).map((member) => [member.personId, member]));
  const organizations = new Map((directory?.organizations ?? []).map((organization) => [organization.id, organization]));
  const roles = new Map(roleOptions.map((role) => [role.roleCode, role]));
  const rootOrganization = directory?.organizations.find((organization) => !organization.parentId);
  const requester = requesterWorkerId ? members.get(requesterWorkerId) : undefined;
  const requesterOrganization = requester?.organizationId ? organizations.get(requester.organizationId) : undefined;
  const requesterLeader = (level: number) => {
    let organization = requesterOrganization;
    let depth = 1;
    const visited = new Set<string>();
    while (organization && !visited.has(organization.id)) {
      visited.add(organization.id);
      if (depth === level) return organization.managerId ?? '';
      organization = organization.parentId ? organizations.get(organization.parentId) : undefined;
      depth += 1;
    }
    return '';
  };
  return steps.map((step) => {
    if (step.resolverType === 'FIXED_ROLE') {
      if (state.rolesLoading) return { order: step.order, loading: true, note: '승인 역할을 불러오는 중입니다.' };
      if (state.rolesError) return { order: step.order, fallbackLabel: '승인 역할 미리보기 불가', note: '승인 역할을 불러오지 못했습니다.' };
      const roleCode = typeof step.resolverConfig?.roleCode === 'string' ? step.resolverConfig.roleCode : '';
      const role = roles.get(roleCode);
      return role
        ? { order: step.order, name: role.descriptor, note: '지정한 역할의 승인자' }
        : { order: step.order, fallbackLabel: roleCode ? '승인 역할을 다시 선택하세요' : '승인 역할 미지정', note: '지정한 역할의 승인자' };
    }
    if (state.directoryLoading) return { order: step.order, loading: true, note: `${approvalDescription(step)} · 조직 정보를 불러오는 중입니다.` };
    if (state.directoryError) return { order: step.order, fallbackLabel: '승인자 미리보기 불가', note: `${approvalDescription(step)} · 조직 정보를 불러오지 못했습니다.` };
    if (!directory) return { order: step.order, fallbackLabel: '승인자 미리보기 불가', note: `${approvalDescription(step)} · 조직 정보가 없습니다.` };
    if (step.resolverType === 'FIXED_PERSON') {
      const personID = typeof step.resolverConfig?.personId === 'string' ? step.resolverConfig.personId : '';
      const member = membersByPerson.get(personID);
      if (!member) return {
        order: step.order,
        fallbackLabel: personID ? '구성원을 다시 선택하세요' : '구성원 미지정',
        note: '지정한 구성원',
      };
      return {
        order: step.order,
        personID: member.personId,
        name: member.name,
        jobTitle: member.title,
        profilePhotoURL: member.profilePhotoUrl,
        note: '지정한 구성원',
      };
    }
    if ((step.resolverType === 'REQUESTER_ORG_LEADER') && !requester) return {
      order: step.order,
      fallbackLabel: '요청자 조직 확인 필요',
      note: `${approvalDescription(step)} · 현재 요청자의 Worker 정보를 확인할 수 없습니다.`,
    };
    let memberID = '';
    if (step.resolverType === 'ROOT_ORG_LEADER') memberID = rootOrganization?.managerId ?? '';
    else if (step.resolverType === 'REQUESTER_ORG_LEADER') memberID = requesterLeader(Number(step.resolverConfig?.level ?? 1));
    const member = members.get(memberID);
    if (!member) return {
      order: step.order,
      fallbackLabel: step.resolverType === 'REQUESTER_ORG_LEADER' || step.resolverType === 'ROOT_ORG_LEADER'
        ? '해당 조직 단계의 조직장 미확정'
        : '제출할 때 승인자 확정',
      note: approvalDescription(step),
    };
    return {
      order: step.order,
      personID: member.personId,
      name: member.name,
      jobTitle: member.title,
      profilePhotoURL: member.profilePhotoUrl,
      note: approvalDescription(step),
    };
  });
}
export function formatWorkflowDate(value: string) {
  const [year, month, day] = formatWorkflowDateKey(value).split('-');
  return year && month && day ? `${year}. ${month}. ${day}.` : value;
}
/** 날짜 전용 업무 값은 유지하고 시각이 있는 값만 업무 시간대로 변환합니다. */
export function formatWorkflowDateKey(value: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return workflowDateIsValid(value) ? value : '';
  return formatTimestampDateKey(value);
}

export function formatWorkflowDateTime(value: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value) || !formatWorkflowDateKey(value)) return formatWorkflowDate(value);
  const time = new Intl.DateTimeFormat('ko-KR', { timeZone: timestampTimeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(value));
  return `${formatWorkflowDate(value)} ${time} (한국 시간)`;
}

export function formatWorkflowDueAt(value: string, now = Date.now()) {
  const dateKey = formatWorkflowDateKey(value);
  if (!dateKey) return value;
  const [year, month, day] = dateKey.split('-');
  const currentYear = formatWorkflowDateKey(new Date(now).toISOString()).slice(0, 4);
  const date = `${year === currentYear ? '' : `${year}/`}${Number(month)}/${Number(day)}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return date;
  const time = new Intl.DateTimeFormat('ko-KR', { timeZone: timestampTimeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(value));
  return `${date} ${time}`;
}
export function fieldOptions(field: WorkflowFieldDefinition) { return Array.isArray(field.config?.options) ? field.config.options as Array<{ value: string; label?: string }> : []; }
export function workflowValidationNumber(value: unknown): number | undefined { return typeof value === 'number' && Number.isFinite(value) ? value : undefined; }
export function workflowValidationMaximumLength(value: unknown): number | undefined {
  const maximum = workflowValidationNumber(value);
  return maximum !== undefined && Number.isInteger(maximum) && maximum > 0 ? maximum : undefined;
}
export function workflowDateIsValid(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}
export function workflowFieldValidationError(field: WorkflowFieldDefinition, value: unknown): string | undefined {
  if (field.type === 'DATE_RANGE') {
    if (value == null) return undefined;
    if (typeof value !== 'object' || Array.isArray(value)) return '날짜 범위를 다시 입력하세요.';
    const range = value as { from?: unknown; to?: unknown };
    const from = typeof range.from === 'string' ? range.from : '';
    const to = typeof range.to === 'string' ? range.to : '';
    if (!from && !to) return undefined;
    if (!from || !to) return '시작일과 종료일을 모두 입력하세요.';
    if (!workflowDateIsValid(from) || !workflowDateIsValid(to)) return '올바른 날짜를 입력하세요.';
    if (to < from) return '종료일은 시작일과 같거나 이후여야 합니다.';
    return undefined;
  }
  if (value == null || typeof value === 'string' && value.trim() === '' || Array.isArray(value) && value.length === 0) return undefined;
  if (field.type === 'FILE') {
    if (!Array.isArray(value)) return '파일을 다시 등록해 주세요.';
    const maximum = Number(field.config?.maxFiles ?? 10);
    if (value.length > maximum) return `파일은 최대 ${maximum}개까지 등록할 수 있습니다.`;
    if (value.some((item) => !item || typeof item !== 'object'
      || typeof (item as { id?: unknown }).id !== 'string'
      || typeof (item as { displayName?: unknown }).displayName !== 'string')) return '파일을 다시 등록해 주세요.';
    return undefined;
  }
  if (field.type === 'WORKFLOW_REFERENCE') {
    if (!Array.isArray(value) || value.length > 20) return '참조 문서는 최대 20개까지 선택할 수 있습니다.';
    const identifiers = value.map((item) => item && typeof item === 'object' ? (item as { id?: unknown }).id : undefined);
    if (identifiers.some((id) => typeof id !== 'string' || !id)) return '참조 문서를 다시 선택해 주세요.';
    if (new Set(identifiers).size !== identifiers.length) return '같은 참조 문서는 한 번만 선택할 수 있습니다.';
    return undefined;
  }
  if (field.type === 'NUMBER' || field.type === 'CURRENCY') {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '숫자를 입력하세요.';
    const minimum = workflowValidationNumber(field.validation?.min);
    const maximum = workflowValidationNumber(field.validation?.max);
    if (minimum !== undefined && value < minimum) return `${minimum} 이상으로 입력하세요.`;
    if (maximum !== undefined && value > maximum) return `${maximum} 이하로 입력하세요.`;
    return undefined;
  }
  const maximumLength = workflowValidationMaximumLength(field.validation?.maxLength);
  if (maximumLength !== undefined && typeof value === 'string' && Array.from(value).length > maximumLength) return `${maximumLength}자 이내로 입력하세요.`;
  return undefined;
}
export function workflowFieldHasValue(field: WorkflowFieldDefinition, value: unknown) {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (field.type === 'WORKFLOW_REFERENCE') return Array.isArray(value) && value.length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (field.type === 'DATE_RANGE') {
    const range = value as { from?: unknown; to?: unknown };
    return typeof range.from === 'string' && range.from.length > 0 && typeof range.to === 'string' && range.to.length > 0;
  }
  return true;
}
